import React from "react";
import { FilterState } from "../../ProjectList/types";
import {
  useFilterDisplayText,
  useFilterRefs,
  useFilterChipFactory,
  useFilterIcons,
  useScrollHandler,
} from "../hooks";
import type { Project } from "@/utils/types";
import { FilterKey } from "@/types/filterEnums";

type FilterValue = string | number;

interface FilterBarProps {
  filters: FilterState;
  currentProject: Project | null;
  onRemoveFilter: (filterType: FilterKey, value: string) => void;
  onRemoveEntireFilter: (filterType: FilterKey) => void;
  onUpdateFilter?: (
    filterType: FilterKey,
    value: FilterValue,
    condition?: string,
  ) => void;
  updateFilters?: (filters: Partial<FilterState>) => void;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  currentProject,
  onRemoveFilter,
  onRemoveEntireFilter,
  onUpdateFilter,
  updateFilters,
}) => {
  const { getFilterDisplayText, userLookupMap, labelLookupMap } =
    useFilterDisplayText({ currentProject });
  const { getOrCreateChipRef } = useFilterRefs({ filters });
  const { getFilterIcon, getValueIcon, getValueIcons } = useFilterIcons({
    userLookupMap,
    labelLookupMap,
  });
  const { scrollContainerRef } = useScrollHandler();

  const { generateFilterChips } = useFilterChipFactory({
    filters,
    currentProject,
    onRemoveFilter,
    onRemoveEntireFilter,
    onUpdateFilter,
    getFilterDisplayText,
    getFilterIcon,
    getValueIcon,
    getValueIcons,
    getOrCreateChipRef,
    updateFilters,
  });

  const filterChips = generateFilterChips();

  if (filterChips?.length === 0) return null;

  return (
    <div className="flex items-center gap-2 p-2 border-b border-secondary-n-900 min-h-[36px]">
      <div
        ref={scrollContainerRef}
        className="flex items-center gap-2 overflow-x-auto overflow-y-hidden scrollbar-hide whitespace-nowrap flex-1"
      >
        {filterChips}
      </div>
    </div>
  );
};
